/**
 * Slot-filling helpers for the bot.
 * Figures out what is missing and asks ONE crisp follow-up at a time.
 *
 * Required to proceed (for draw_rectangle):
 *   - cell
 *   - layer
 *   - geometry: either (placement + size) OR (two_points)
 */

import ProjectManager from "../../canvas/ProjectManager";

// Return array of missing keys in priority order
export function fillMissing(state) {
  const need = [];
  if (!state?.slots) return ["cell", "layer", "geometry"];

  if (state.intent === "delete_rectangle") {
    return fillMissingDelete(state);
  }

  // Cell: default to currently selected cell if available
  if (!state.slots.cell) {
    const sel =
      safe(ProjectManager, "getSelectedCell")?.() ||
      safe(ProjectManager, "getActiveCellName")?.();
    if (sel) state.slots.cell = sel;
    else need.push("cell");
  }

  // Layer: must be present (NLU may set a canon name or numeric pair)
  if (!state.slots.layer) need.push("layer");

  // Geometry: either two points OR placement + size
  const p = state.slots.placement;
  const s = state.slots.size;
  const isTwoPoints = p?.mode === "two_points" && p?.p1 && p?.p2;

  // Size may be "square", or w/h (both needed), with optional units
  const hasSize =
    !!s &&
    ((typeof s.square === "number" && isFinite(s.square)) ||
      (isFiniteNumber(s.w) && isFiniteNumber(s.h)));

  const hasPlacement =
    !!p && (p.mode === "origin" || p.mode === "center" || p.mode === "two_points");

  if (!(isTwoPoints || (hasPlacement && hasSize))) need.push("geometry");

  return need;
}

// Turn a missing key (or ui error code) into a follow-up assistant message
export function followupFor(key, state) {
  const lang = state?.language || "hinglish";
  const sel =
    safe(ProjectManager, "getSelectedCell")?.() ||
    safe(ProjectManager, "getActiveCellName")?.() ||
    "none";

  const T = {
    // Missing slots
    cell: {
      hi: `Kaun se cell me banana hai? (current: ${sel})`,
      en: `Which cell should I use? (current: ${sel})`,
      hinglish: `Kis cell me banaun? (current: ${sel})`,
    },
    layer: {
      hi: "Kaunsi layer? (e.g., M1, NW1, 8:0).",
      en: "Which layer? (e.g., M1, NW1, 8:0).",
      hinglish: "Kaunsi layer? (e.g., M1, NW1, 8:0).",
    },
    geometry: {
      hi: "Position/size batado — e.g. `0,0 par 2um x 1um` ya `from 0,0 to 10,5`.",
      en: "Provide position/size — e.g. `at 0,0 2um x 1um` or `from 0,0 to 10,5`.",
      hinglish:
        "Position/size de do — e.g. `0,0 pe 2um x 1um` ya `from 0,0 to 10,5`.",

  delete_target: {
     hi: "Kya delete karun? (selected / ids 1,2 / region from x1,y1 to x2,y2 / size 5 by 5 + layer)",
     en: "What should I delete? (selected / ids 1,2 / region from x1,y1 to x2,y2 / size 5 by 5 + layer / layer M1)",
     hinglish: "Kya delete karu? (selected / ids 1,2 / region from x1,y1 to x2,y2 / size 5 by 5 + layer)",
   },
    },

    // UI validation errors
    layer_not_found: {
      hi: "Ye layer nahi mili. Valid layer batao (e.g., M1, NW1, 8:0).",
      en: "Layer not found. Please specify a valid layer (e.g., M1, NW1, 8:0).",
      hinglish:
        "Layer nahi mili. Valid layer bolo (e.g., M1, NW1, 8:0).",
    },
    layer_locked: {
      hi: "Layer locked hai. Unlock karu?",
      en: "Layer is locked. Should I unlock it?",
      hinglish: "Layer locked hai. Unlock karu?",
    },
    zero_size: {
      hi: "Width/height zero ya invalid hai. Size dubara batao (e.g., 2um x 1um).",
      en: "Width/height is zero or invalid. Please give size again (e.g., 2um x 1um).",
      hinglish:
        "Width/height zero ya invalid hai. Size fir se bolo (e.g., 2um x 1um).",
    },
  };

  const txt =
    T[key]?.[lang] ??
    (lang === "hi"
      ? "Thoda aur clear batao."
      : lang === "en"
      ? "Please clarify."
      : "Thoda aur clear bolo.");
  return { role: "assistant", text: txt };
}

/* ───────────────────────────── Helpers ───────────────────────────── */

function fillMissingDelete(state){
  const need=[];
  // cell: default current
  if (!state.slots.deletion?.cell) {
    const sel = safe(ProjectManager,"getSelectedCell")?.() || safe(ProjectManager,"getActiveCellName")?.();
    if (sel) { state.slots.deletion = { ...(state.slots.deletion||{}), cell: sel }; }
    else need.push("cell");
  }
  // target: at least one of [selected | ids | region | (layer + size)]
  const d = state.slots.deletion || {};
  const hasTarget = d.selected || (d.ids?.length) || d.region || d.size || state.slots.layer;
  if (!hasTarget) {
    // ask minimal: region or selected?
    need.push("delete_target");
  }
  // layer optional unless using size-filter
  if (d.size && !state.slots.layer) need.push("layer");
  return need;
}


function safe(obj, key) {
  try {
    const fn = obj?.[key];
    return typeof fn === "function" ? fn.bind(obj) : null;
  } catch {
    return null;
  }
}

function isFiniteNumber(n) {
  return typeof n === "number" && isFinite(n);
}

export default { fillMissing, followupFor };
